"use client"

import styled from "@emotion/styled"
import { Button, Typography } from "@mui/material"
import Image from "next/image"
import { useState } from "react"
import { sharedColors } from "ui/theme/colors"
import { extractProperty, getNonUmbracoUrl, Link } from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import ReactPlayerComponent from "../Homepage/Hero/ReactPlayer"
import VideoControls from "../Homepage/Hero/VideoControls"
import BusAnimation from "./BusAnimation"
import Form from "./Form"

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`

const TopBanner = styled.div`
  display: grid;
  width: 100%;
  text-align: center;
  ${(props) => props.theme.breakpoints.up("md")} {
    grid-template-columns: 1fr 1fr;
  }
`

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 360px;
  ${(props) => props.theme.breakpoints.up("md")} {
    /* height: 800px;
    width: 720px; */
    width: 100%;
    height: auto;
    aspect-ratio: 720/800;
    order: 2;
  }
`

const BottomTextBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
  padding: 0 16px;
`

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 48px 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    order: 1;
  }
`

const StyledTypography = styled(Typography)`
  p {
    margin: 0;
  }
`

const MiddleContent = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${sharedColors.brandDark};
  gap: 32px;
  padding: 40px 0;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 80px;
    padding: 80px 0;
  }
`

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  width: 100%;
  padding: 0 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 24px;
    grid-template-columns: repeat(6, 1fr);
    width: fit-content;
    align-self: center;
  }
`

const CardLayout = styled.div`
  display: flex;
  padding: 24px 16px;
  flex-direction: column;
  height: 172px;
  width: 100%;
  align-items: center;
  text-align: center;
  gap: 12px;
  border-radius: 12px;
  background-color: white;
  border: 1px solid ${sharedColors.borderGray};
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 16px;
    height: 158px;
    width: 186px;
  }
`

const MediaContainer = styled.div`
  width: 100%;
  height: 310px;
  min-height: 310px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  ${(props) => props.theme.breakpoints.up("md")} {
    height: 583px;
    min-height: 583px;
  }
`

const StyledButton = styled(Button)`
  display: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    max-width: 300px;
  }
`

const StyledTitle = styled(Typography)`
  font-size: 32px !important;
  line-height: 150% !important;
  ${(p) => p.theme.breakpoints.up("md")} {
    font-size: 54px !important;
    line-height: 115% !important;
  }
`
export default function DesignB({
  data,
  locale,
  tooltipText,
}: {
  data: any
  locale: string
  tooltipText: string
}) {
  const [playing, setPlaying] = useState(true)
  const imageNextToText = extractProperty(data, "imageNextToText")
  const logo = extractProperty(data, "logo")

  const title = extractProperty(data, "title")
  const description = extractProperty(data, "description")
  const subTitle = extractProperty(data, "subTitle")
  const text = extractProperty(data, "text")
  const iconListBlock = extractProperty(data, "iconList") || []
  const imageForDesktop = extractProperty(data, "imageForDesktop")
  const imageForMobile = extractProperty(data, "imageForMobile")
  const video = extractProperty(data, "video")
  const url = getUmbracoMediaUrl(video)
  const contactButton = extractProperty(data, "contactButton")?.[0]?.name
  const form = extractProperty(data, "form")
  const iconList =
    iconListBlock.map((el: any) => ({
      icon: extractProperty(el, "icon"),
      text: extractProperty(el, "text"),
    })) || []
  const legalText = extractProperty(data, "legalText")
  const formTitle = extractProperty(form?.[0], "formTitle")
  const formLogo = extractProperty(form?.[0], "formLogo")
  const termsConfirmationText = extractProperty(
    form?.[0],
    "termsConfirmationText",
  )
  const scrollToForm = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    const formElement = document.getElementById("form")
    if (formElement) {
      formElement.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }
  const isVideo =
    url.includes(".mp4") || url.includes("youtube") || url.includes("youtu.be")

  return (
    <StyledLayout>
      <TopBanner>
        <ImageContainer>
          <Image
            src={getNonUmbracoUrl(imageNextToText)}
            alt=""
            fill
            style={{ objectFit: "cover" }}
          />
        </ImageContainer>
        <TextContainer>
          <Image src={getNonUmbracoUrl(logo)} alt="" width={80} height={80} />
          <StyledTitle variant="h1">{title}</StyledTitle>
          <StyledTypography
            component={"div"}
            variant="h3"
            fontWeight={400}
            dangerouslySetInnerHTML={{ __html: description || "" }}
          />
          <StyledButton
            variant="primary"
            size="large"
            href="#form"
            LinkComponent={Link}
            onClick={scrollToForm}
            style={{ marginTop: 16 }}
            fullWidth
          >
            {contactButton}
          </StyledButton>
        </TextContainer>
      </TopBanner>
      <MiddleContent>
        <BottomTextBox>
          <Typography variant="h1" color="#F2D902">
            {subTitle}
          </Typography>
          <StyledTypography
            component="div"
            variant="h4"
            color="white"
            fontWeight={400}
            dangerouslySetInnerHTML={{ __html: text }}
          />
        </BottomTextBox>
        <CardsGrid>
          {iconList.map((el: any) => (
            <CardLayout key={el.text}>
              <Image src={el.icon} alt={""} width={56} height={56} />
              <Typography variant="body1">
                {el.text?.replace(/\u2028/g, " ")}
              </Typography>
            </CardLayout>
          ))}
        </CardsGrid>
        <BusAnimation locale={locale} template="B" />
      </MiddleContent>
      {isVideo ? (
        <MediaContainer>
          <VideoControls
            playing={playing}
            setPlaying={setPlaying}
            centerHorizontally={true}
            hideOnPlay={true}
          />

          <ReactPlayerComponent
            source={url}
            playing={playing}
            volume={false}
            setPlaying={setPlaying}
            setVolume={() => {}}
          />
        </MediaContainer>
      ) : url ? (
        <MediaContainer>
          <Image src={url} alt={title} fill style={{ objectFit: "cover" }} />
        </MediaContainer>
      ) : null}
      <Form
        logo={formLogo}
        designB={true}
        formTitle={formTitle}
        termsConfirmationText={termsConfirmationText}
        legalText={legalText}
        tooltipText={tooltipText}
      />
    </StyledLayout>
  )
}
